import { z } from "zod";
import { MCP_SERVER_NAME, MCP_TOOLSET_DEFAULT_POLICY, mcpServerUrlIssue } from "./agent-mcp.js";
import { canonicalIdPattern } from "./canonical-ids.js";
import { LimitationSchema, RequirementSchema } from "./capabilities.js";
import { AccountRoleSchema } from "./identity.js";
import { reservedToolNameIssue } from "./multiagent.js";
import { CANONICAL_FILE_ID } from "./outcome.js";
import { ENGINE_TARGETS, RuntimeProviderSchema } from "./runtime.js";
import { LocaleSchema, SiteIdSchema } from "./sites.js";
export const RunSubjectSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("owner") }),
    z.object({ kind: z.literal("profile"), profileId: z.string().min(1) }),
]);
export const EffectivePolicySchema = z.object({
    allowedModels: z.array(z.string().min(1)),
    disabledTools: z.array(z.string().min(1)),
    allowMcp: z.boolean(),
    allowWebFetch: z.boolean(),
    forceHitl: z.boolean(),
    // Template Strategy V2 phase 4c: the runtime providers this subject may
    // reserve on (plan ∩ account ∩ profile) and the plan's preference order over
    // the site routing order (null = the site order). Server-resolved like every
    // other field; the client reads them only to pre-select / grey out profiles
    // — presentation never grants permission.
    runtimeProvidersAllowed: z.array(RuntimeProviderSchema),
    runtimeProviderOrder: z.array(RuntimeProviderSchema).nullable(),
});
export const ViewerContextSchema = z.object({
    siteId: SiteIdSchema,
    principalId: z.string().min(1),
    accountId: z.string().min(1),
    accountRole: AccountRoleSchema,
    subject: RunSubjectSchema,
    locale: LocaleSchema,
    audienceBand: z.string().min(1).optional(),
    effectivePolicy: EffectivePolicySchema,
});
export const BrandTokensSchema = z.object({
    name: z.string().min(1),
    shortName: z.string().min(1),
    logo: z.string().min(1),
    colors: z.object({
        ink: z.string().min(1),
        surface: z.string().min(1),
        primary: z.string().min(1),
        accent: z.string().min(1),
        success: z.string().min(1),
        danger: z.string().min(1),
    }),
});
export const ProjectSchema = z.object({
    id: z.string().min(1),
    siteId: SiteIdSchema,
    accountId: z.string().min(1),
    subject: RunSubjectSchema,
    name: z.string().min(1).max(160),
    revision: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /**
     * Template Strategy V2 phase 3: the provider/model profile the person chose
     * for this project (`<provider>/<canonical>`); null = no preference yet
     * (reservation falls back to the site default). Optional on the wire while
     * older clients learn the field.
     */
    preferredProviderProfile: z.string().nullable().optional(),
    /**
     * The catalog version the project's documents come from (projects.template_version_id): the
     * one projects.create copied (with `exactVersion`, the named one even when retired) or the
     * one the last template upgrade applied; null for a project started empty. Optional on the
     * wire: a server before 5.16.0 does not name it.
     */
    templateVersionId: z
        .string()
        .uuid()
        .nullable()
        .optional()
        .describe("The catalog version the project's documents come from: the one projects.create copied or the last template upgrade applied; null for a project started empty."),
});
export const PermissionPolicySchema = z.enum(["always_allow", "always_ask"]);
// Mirrored on the studio island at agent-studio/contracts/src/flat-yaml.ts —
// keep the two lists in sync by hand (no legal shared home crosses the boundary).
export const BUILTIN_TOOL_NAMES = [
    "web_search",
    "web_fetch",
    "read",
    "write",
    "edit",
    "bash",
    "glob",
    "grep",
];
/**
 * The platform permission default when a spec omits it: always_allow for every
 * builtin EXCEPT bash (owner decision 2026-08-12, mirroring the allow-except-
 * bash security policy of agent-studio's flat-YAML defaults, owner decision
 * 2026-07-21). MCP servers keep ask-by-default — external side effects retain
 * the human gate.
 */
export function defaultBuiltinToolPermission(name) {
    return name === "bash" ? "always_ask" : "always_allow";
}
const BuiltinToolObjectSchema = z.object({
    name: z.enum(BUILTIN_TOOL_NAMES),
    enabled: z.boolean(),
    permission: PermissionPolicySchema,
    /**
     * Template Strategy V2 phase 6: `optional` marks a capability the agent
     * can live without — a provider that lacks it stays runnable with a visible
     * limitation ("Runs without web search") instead of being Locked. Absent =
     * required (every pre-universal spec). Lowered from the universal
     * document's requirements; authors of flat v2 specs may set it too.
     */
    requirement: RequirementSchema.optional(),
});
// A static .default() cannot express allow-except-bash, so the conditional
// default rides a preprocess: only an OMITTED permission is filled in; an
// explicit value (either way) always wins.
export const BuiltinToolSchema = z.preprocess((value) => {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return value;
    const tool = value;
    if (tool.permission !== undefined)
        return value;
    return { ...tool, permission: defaultBuiltinToolPermission(String(tool.name)) };
}, BuiltinToolObjectSchema);
/**
 * MCP URL hygiene is the agent document's rule (agent-mcp.ts): https on port
 * 443 to a named host, never userinfo; a key may ride in the query. V2's
 * http-on-localhost allowance is gone — no V5 engine can reach it.
 */
const mcpServerUrlSchema = z.string().superRefine((value, context) => {
    const issue = mcpServerUrlIssue(value);
    if (issue)
        context.addIssue({ code: "custom", message: `MCP server URL: ${issue.message}` });
});
export const McpServerSchema = z.object({
    name: z.string().regex(MCP_SERVER_NAME),
    url: mcpServerUrlSchema,
    enabled: z.boolean().default(true),
    permission: PermissionPolicySchema.default(MCP_TOOLSET_DEFAULT_POLICY),
});
/**
 * Names the LOOP's platform tools own (D1/D2 + platform web, loop-v3; the
 * edit overlay, loop-v4; the shell sandbox, loop-v5): a spec-authored custom
 * tool wearing one would shadow the platform executor at dispatch (platform is
 * classified before custom) and could hijack metering. Kept here (not imported
 * from domain) because contracts sits below domain.
 */
export const RESERVED_PLATFORM_TOOL_NAMES = [
    "save_output",
    "memory_read",
    "memory_write",
    "web_search",
    "web_fetch",
    "edit_file",
    "run_shell",
];
export const CustomToolSchema = z.object({
    // The mcp__ prefix is RESERVED for runtime-generated MCP tool names (v1.1
    // Stage 3): a spec-authored tool wearing it could shadow a discovered tool
    // in the model's toolbox and hijack its dispatch identity. The platform
    // tool names are reserved for the same reason (verified against the live
    // catalog: no stored spec uses any of them).
    name: z
        .string()
        .regex(/^[A-Za-z][A-Za-z0-9_-]*$/)
        .refine((name) => !name.startsWith("mcp__"), {
        message: "the mcp__ prefix is reserved for MCP tools",
    })
        .refine((name) => reservedToolNameIssue(name) === null, {
        message: "the ant__ prefix is reserved by Anthropic Managed Agents",
    })
        .refine((name) => !RESERVED_PLATFORM_TOOL_NAMES.includes(name), {
        message: "this name is reserved for a platform tool",
    }),
    description: z.string().min(1),
    inputSchema: z.record(z.string(), z.unknown()),
});
/**
 * Canonical definition of the check-in tool. This is the single source of
 * truth for authoring surfaces and the domain compiler, which injects it at
 * compile time on BOTH sites when a spec doesn't carry its own. A
 * spec-defined check_with_me always wins over injection, so template authors
 * may override the description; existing saved specs keep working with
 * question-only input. The default description below is MFA's wording and
 * must stay byte-identical (it participates in compiled-executable cache
 * identity); LYA compiles inject CHECK_WITH_ME_TOOL_DESCRIPTION_PROFESSIONAL
 * instead.
 */
export const CHECK_WITH_ME_TOOL = {
    name: "check_with_me",
    description: 'The only way to ask the learner anything. Call it whenever you need an answer, a choice, a go-ahead, or a decision before continuing — a question typed as plain message text never reaches the learner. The run pauses (requires_action) until they reply. Put ONE direct, self-contained question in `question` (never a preamble like "Great, go ahead and…" — ask the question itself); put brief explanation or the expected answer format in `context`. Pick `responseMode` deliberately: "text" when they should type the answer; "choice" with 2-4 options that are each COMPLETE answers (never meta-actions like "I\'ll type it next" or "Other" — typing is always available); "confirmation" ONLY when `question` names the concrete action you are about to take and a plain go-ahead/no decides it. In "text" mode you may ALSO offer 1-3 `quickReplies` — short COMPLETE answers a person often gives here (for example "No sources" or "Skip this"), shown as tap-able buttons beside the typing box; never meta-actions, never a restatement of the question. Never end your turn with a plain-text question instead of calling this tool.',
    inputSchema: {
        type: "object",
        properties: {
            question: { type: "string", minLength: 1 },
            context: { type: "string" },
            responseMode: { type: "string", enum: ["text", "choice", "confirmation"] },
            choices: {
                type: "array",
                items: { type: "string", minLength: 1 },
                minItems: 2,
                maxItems: 4,
            },
            // v9 (2026-08-22): optional escape-hatch answers for a "text" question
            // ("No sources", "Skip this") — tap-able chips beside the composer. The
            // contract for `choices` (2-4 COMPLETE answers, choice mode) is untouched.
            quickReplies: {
                type: "array",
                items: { type: "string", minLength: 1 },
                minItems: 1,
                maxItems: 3,
            },
        },
        required: ["question", "responseMode"],
        additionalProperties: false,
    },
};
/**
 * Whether an AUTHORED check_with_me description knows the platform's response
 * contract (agent-spec v8+: it names `responseMode`). Templates seeded before
 * v8 (migrations 032/057/061) carry their own check_with_me whose wording
 * predates the field, and the model they prompt never learns to ask for
 * buttons — the compiler replaces such a description with the canonical one
 * (authored v8-aware wording still wins; see domain runtimeToolSlice).
 */
export function checkWithMeDescriptionIsCurrent(description) {
    return description.includes("responseMode");
}
export const CHECK_WITH_ME_TOOL_NAME = CHECK_WITH_ME_TOOL.name;
/** The response contract a check_with_me call declares for its question. */
export const CHECK_WITH_ME_RESPONSE_MODES = ["text", "choice", "confirmation"];
/**
 * Professional-voice description injected by LYA compiles ("the person
 * you're working with", not "the learner"). Same contract, same schema —
 * only the audience wording differs.
 */
export const CHECK_WITH_ME_TOOL_DESCRIPTION_PROFESSIONAL = 'The only way to ask the person you\'re working with anything. Call it whenever you need an answer, a choice, a go-ahead, or a decision before continuing — a question typed as plain message text never reaches them. The run pauses (requires_action) until they reply. Put ONE direct, self-contained question in `question` (never a preamble like "Great, go ahead and…" — ask the question itself); put brief explanation or the expected answer format in `context`. Pick `responseMode` deliberately: "text" when they should type the answer; "choice" with 2-4 options that are each COMPLETE answers (never meta-actions like "I\'ll type it next" or "Other" — typing is always available); "confirmation" ONLY when `question` names the concrete action you are about to take and a plain go-ahead/no decides it. In "text" mode you may ALSO offer 1-3 `quickReplies` — short COMPLETE answers a person often gives here (for example "No sources" or "Skip this"), shown as tap-able buttons beside the typing box; never meta-actions, never a restatement of the question. Never end your turn with a plain-text question instead of calling this tool.';
export const OutcomeDefinitionSchema = z.object({
    description: z.string().min(1),
    criteria: z.array(z.string().min(1)).default([]),
    maxIterations: z.number().int().min(1).max(20).default(3),
    // V1 parity: the "done looks like" rubric can come from an uploaded
    // workspace file instead of inline text; when set, the run's
    // define_outcome carries the (lazily pushed) provider file reference.
    // Canonical browser-visible id form only — provider ids never appear here.
    rubricFileId: z.string().regex(CANONICAL_FILE_ID).optional(),
});
/** V1-ported goal helper ("help me"): rough idea in, mission + rubric out. */
export const GoalHelpRequestSchema = z.object({
    // A blank idea is allowed: the helper then drafts a mission from the
    // project's own system prompt + tools instead of rewriting kid text.
    idea: z.string().max(12_000).default(""),
    graderIds: z.array(z.string().min(1).max(80)).max(30).default([]),
});
export const GoalHelpResponseSchema = z.object({
    mission: z.string().max(1_000),
    rubric: z.string().max(4_000),
    graderIds: z.array(z.string()).max(10),
});
/** Curated MCP catalog entry (code-managed, as in V1 — not a DB table). */
export const McpLibraryEntrySchema = z.object({
    id: z.string().min(1),
    serverName: z.string().min(1),
    name: z.string().min(1),
    url: z.string().url(),
    defaultAsk: z.boolean(),
    active: z.boolean(),
    toolsPreview: z.array(z.string()),
    blurb: z.string(),
});
export const McpLibraryTestResultSchema = z.object({
    ok: z.boolean(),
    latencyMs: z.number().int().nonnegative().optional(),
    tools: z.array(z.string()).optional(),
    error: z.string().optional(),
});
export const OutcomeStatusSchema = z.enum([
    "satisfied",
    "needs_revision",
    "failed",
    "interrupted",
    "max_iterations_reached",
    "unknown",
]);
/**
 * Turn-terminal run statuses — the single source shared by the API workers,
 * route guards, and the web console. A terminal TURN does not imply a dead
 * session: an idle session whose latest turn ended can still be resumed.
 */
export const TERMINAL_RUN_STATUS_LIST = [
    "satisfied",
    "needs_revision",
    "failed",
    "interrupted",
    "max_iterations_reached",
];
export const TERMINAL_RUN_STATUSES = new Set(TERMINAL_RUN_STATUS_LIST);
export function isTerminalRunStatus(status) {
    return TERMINAL_RUN_STATUSES.has(status);
}
/**
 * The complete runs.status vocabulary — exactly the database CHECK constraint
 * (migration 002): three in-flight states plus the terminal set. Anything the
 * UI renders beyond these is a client-side presentation state, not a run
 * status.
 */
export const RUN_STATUS_LIST = [
    "preparing",
    "running",
    "requires_action",
    ...TERMINAL_RUN_STATUS_LIST,
];
export const RunStatusSchema = z.enum(RUN_STATUS_LIST);
export const StartRunCommandSchema = z.object({
    projectId: z.string().min(1),
    draftRevision: z.number().int().positive(),
    mission: z.string().min(1).max(20_000),
    outcome: OutcomeDefinitionSchema.optional(),
});
/**
 * Follow-up turn on a completed run's parent session. The new turn reuses the
 * session's original resolved configuration (conversation, sandbox, model);
 * the message becomes the turn's mission.
 */
export const ResumeRunCommandSchema = z.object({
    message: z.string().min(1).max(20_000),
});
const ToolDecisionEventIdsSchema = z
    .array(z.string().min(1).max(500))
    .min(1)
    .max(100)
    .refine((ids) => new Set(ids).size === ids.length, {
    message: "Tool decision event ids must be unique",
});
export const RunCommandSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("message"),
        commandId: z.string().uuid(),
        text: z.string().min(1).max(20_000),
    }),
    z.object({
        type: z.literal("interrupt"),
        commandId: z.string().uuid(),
        /**
         * B5: stop one child thread (the Session threads page's `session_thread_id`), not the
         * session. Absent = every thread and the primary. A thread no `session.thread_created` named
         * is 404 thread_not_found, a terminated one 400 thread_terminated, and a workflow run's
         * thread while its run is open 400 workflow_run_open (the page: a run's thread cannot be
         * stopped by its id).
         */
        threadId: z.string().min(1).max(200).optional(),
    }),
    /**
     * B5: archive one child thread, which frees its place under the session's 25-child cap.
     * Only an idle thread can be archived (one parked on `requires_action` counts as idle); a
     * running one is 400 thread_running until it is interrupted, and a workflow run's thread
     * while its run is open is 400 workflow_run_open (the server archives a run's threads).
     */
    z.object({
        type: z.literal("archive_thread"),
        commandId: z.string().uuid(),
        threadId: z.string().min(1).max(200),
    }),
    z
        .object({
        type: z.literal("tool_decision"),
        commandId: z.string().uuid(),
        eventIds: ToolDecisionEventIdsSchema,
        approved: z.boolean(),
        // Optional reason forwarded upstream as deny_message so the agent can
        // pick a safer path; meaningful only when approved is false.
        denyMessage: z.string().min(1).max(2_000).optional(),
        // "run" is Always allow: the run stops asking for these tools until it
        // ends (the agent's saved Ask is untouched). Absent means "once".
        scope: z.enum(["once", "run"]).optional(),
    })
        .refine((decision) => decision.scope !== "run" || decision.approved, {
        message: "Only an approval can cover the rest of the run",
        path: ["scope"],
    }),
    z.object({
        type: z.literal("custom_tool_result"),
        commandId: z.string().uuid(),
        toolUseId: z.string().min(1),
        result: z.unknown(),
        isError: z.boolean().default(false),
    }),
]);
/** A session thread's status, as the Session threads page lists it (`requires_action` is an idle). */
export const RUN_THREAD_STATUS_LIST = ["running", "idle", "rescheduling", "terminated"];
export const RunThreadStatusSchema = z.enum(RUN_THREAD_STATUS_LIST);
/**
 * One thread of a run's session as the Session threads page lists it (B5, runs.threads): the
 * primary (`parentThreadId` null) and every child, archived ones included (`terminated`, with
 * `archivedAt`). `agent` is the thread's snapshot: an agent by name, or the advisor by model.
 * `workflowRunId` names the workflow run that started the thread, null on every other one.
 * `usage` is null until the thread's first idle; `costCents` is null while it has no list price.
 */
export const RunThreadSchema = z.object({
    id: z.string().min(1),
    agent: z.object({
        type: z.enum(["agent", "advisor", "inline"]),
        name: z.string().nullable(),
        model: z.string().nullable(),
    }),
    status: RunThreadStatusSchema,
    parentThreadId: z.string().nullable(),
    workflowRunId: z.string().nullable(),
    createdAt: z.string().nullable(),
    archivedAt: z.string().nullable(),
    usage: z
        .object({
        inputTokens: z.number().nonnegative(),
        outputTokens: z.number().nonnegative(),
        cacheReadInputTokens: z.number().nonnegative(),
        costCents: z.number().nullable(),
    })
        .nullable(),
});
/**
 * The 409 codes a tool decision or custom tool result gets when its call is settled without
 * it, which the console reconciles as settled rather than as a failure: something already
 * answered the call, the call is not an ask the run is waiting on, or the call's thread
 * terminated, so an answer has no effect (`params.threadId` names the thread). A fourth,
 * `command_target_in_flight`, is not settled: the server is answering it right now.
 */
export const SETTLED_COMMAND_TARGET_CODES = [
    "command_target_already_answered",
    "invalid_command_target",
    "command_target_closed",
];
export const RunEventEnvelopeSchema = z.object({
    id: z.string().min(1),
    runId: z.string().min(1),
    sessionId: z.string().min(1),
    sequence: z.number().int().nonnegative(),
    occurredAt: z.string().datetime(),
    source: z.enum(["user", "agent", "platform", "proxy"]),
    type: z.string().min(1),
    payload: z.unknown(),
    /**
     * The `commandId` of the runs.command call this user event was sent for, in lowercase (owner
     * decision D84, wave H1b): a client matches the row it echoed while the call was in flight to
     * this one exactly. Absent on every other row: the platform's and the agent's, the mission a
     * run starts with, an answer the server's Always allow sent, a row from before the field, and
     * one the engine could not tie to its send (a restart in between), which a client matches as
     * before, by type and content.
     */
    commandId: z.string().uuid().optional(),
});
/**
 * Ephemeral live-preview frame relayed from the upstream stream. Sent on the
 * browser SSE channel WITHOUT an SSE id: previews never advance
 * Last-Event-ID, never enter history or Raw, and are best-effort only —
 * the authoritative agent.message envelope always follows.
 */
export const RunPreviewFrameSchema = z.object({
    runId: z.string().min(1),
    previewId: z.string().min(1),
    index: z.number().int(),
    text: z.string(),
    done: z.boolean(),
});
/** The run stream's wire version, as its `state` frame carries it. */
export const RUN_STREAM_WIRE = 1;
/**
 * The run stream's first frame (`event: state`, no `id:`). The server writes every field: the
 * wire version, the log's head when the stream opened (the last stored sequence, -1 before
 * any) and the run's status then. `wire`, `head` and `status` are optional only so a reader of
 * the frame from before them still parses.
 */
export const RunStreamStateSchema = z.object({
    stream: z.enum(["live", "polling"]),
    wire: z.literal(RUN_STREAM_WIRE).optional(),
    head: z.number().int().min(-1).optional(),
    status: RunStatusSchema.optional(),
});
/**
 * The run stream's last frame (`event: end`, no `id:`), written only to a stream opened with
 * `?until=settled`, once the run is terminal and no workflow run is open: its terminal status,
 * the log position the stream read through (resume after it to miss nothing), and the sequence
 * of the log's last event when the run was found settled. The status describes the log up to that
 * event, and the stream wrote every event up to it before the end, so a client that has folded an
 * event at or past `lastSequence` holds what the end describes. `lastSequence` is optional only so
 * a reader of the frame from before it still parses; the server always writes it.
 */
export const RunStreamEndSchema = z.object({
    status: RunStatusSchema,
    throughSequence: z.number().int().min(-1),
    lastSequence: z
        .number()
        .int()
        .min(-1)
        .optional()
        .describe("The sequence of the log's last event (the last stored row the stream carries; -1 before any) when the run was found settled: the status describes the log up to it."),
});
export const ProviderSessionStatusSchema = z.enum([
    "running",
    "idle",
    "rescheduling",
    "terminated",
    "unknown",
]);
export const RunFinalizationStateSchema = z.enum(["not_started", "pending", "complete", "failed"]);
/**
 * The run's parent-session view: provider lifecycle (kept separate from the
 * turn outcome), turn position, staleness, and the resume gate that decides
 * when a follow-up turn may start. Identifiers are canonical-only — the
 * upstream session id never reaches the browser. The capability flags
 * (canSend/canInterrupt/canResume/canStartNew) and the finalization STATE are
 * deliberately absent: they live at the RunDetail top level, where they stay
 * meaningful for a run that has no session yet.
 */
export const RunSessionInfoSchema = z.object({
    providerStatus: ProviderSessionStatusSchema,
    turnNumber: z.number().int().positive(),
    turnCount: z.number().int().positive(),
    isActiveTurn: z.boolean(),
    stale: z.boolean(),
    finalization: z.object({
        resumeReady: z.boolean(),
    }),
    /**
     * Live toolbelt sync status: whether the saved Tools-panel configuration has
     * reached this session ("applied"), is waiting for the next idle/follow-up
     * ("pending"), or could not be computed ("unknown" — run detail never fails
     * over this field). Null when the session can no longer receive updates
     * (terminated) or this run is not the session's active turn.
     */
    toolSync: z
        .object({
        state: z.enum(["applied", "pending", "unknown"]),
        lastAppliedAt: z.string().nullable(),
    })
        .nullable(),
});
/**
 * Per-turn usage and cost. 'estimated' is the live event-derived figure,
 * 'settled' comes from provider-reconciled settlement, 'provider_reported' is
 * the exact figure the run's own ledger holds (a service run's façade
 * session, in micro-cents), 'unavailable' means tokens were spent but no
 * pricing row exists (the hold is charged and the UI must show "cost
 * unavailable" rather than a fabricated number).
 */
/**
 * One agent's share of a run's spend (B6): the threads of one kind and agent name (the
 * advisor's and the grader's have none), at list price. `costCents` is null when any of its
 * threads has no price yet. The shares have no model and need not sum to the run's total
 * (the session's runtime and the harness searches are the session's own).
 */
export const AgentSpendSchema = z.object({
    kind: z.enum(["primary", "helper", "advisor", "grader"]),
    agentName: z.string().nullable(),
    threads: z.number().int().nonnegative(),
    costCents: z.number().nullable(),
    tokens: z.number().nonnegative(),
});
/** One workflow run's spend: the sum over the threads it started (null when one has no price yet). */
export const WorkflowRunSpendSchema = z.object({
    workflowRunId: z.string(),
    costCents: z.number().nullable(),
    tokens: z.number().nonnegative(),
});
export const RunUsageSchema = z.object({
    state: z.enum(["estimated", "settled", "provider_reported", "unavailable"]),
    settled: z.boolean(),
    totalCostCents: z.number().nullable(),
    tokenCostCents: z.number().nullable(),
    toolCostCents: z.number(),
    runtimeCostCents: z.number(),
    modelCalls: z.number().int().nonnegative(),
    graderCalls: z.number().int().nonnegative(),
    totalTokens: z.number().nonnegative(),
    agentTokens: z.number().nonnegative(),
    graderTokens: z.number().nonnegative(),
    webSearchRequests: z.number().int().nonnegative(),
    runtimeMs: z.number().nonnegative(),
    creditsCharged: z.number().int().nullable(),
    /** Each agent's share, from the latest usage row's per-thread spend; absent when the row has none. */
    byAgent: z.array(AgentSpendSchema).optional(),
    /** Each workflow run's spend, from the same rows; absent when the row has no per-thread spend. */
    byWorkflowRun: z.array(WorkflowRunSpendSchema).optional(),
});
/**
 * The run shape everywhere a run is listed or freshly created: what the API's
 * publicRun() emits — POST /runs, POST /runs/:id/resume, and each entry of
 * GET /projects/:id/runs. Detail-only fields live on RunDetailSchema.
 */
export const RunSummarySchema = z.object({
    id: z.string().min(1),
    projectId: z.string().min(1),
    subject: RunSubjectSchema,
    status: RunStatusSchema,
    sessionLogId: z.string().nullable(),
    mission: z.string(),
    stopReason: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    turnNumber: z.number().int().positive().nullable(),
    /** The finished run this one was replayed from (its kept transcript seeded this conversation), when it was. */
    replayedFrom: z.string().nullable().optional(),
    /**
     * The runner the run took (`runs.engine_target`, the service-routing plan §3.4): Anthropic,
     * the server's loop or the sandbox service. Null on a row from a revision before it was
     * recorded; optional on the wire while clients learn it.
     */
    engineTarget: z.enum(ENGINE_TARGETS).nullable().optional(),
    /** Why the deployment's rule sent the run to the loop (the shapes the service lacked); null when it did not decide so. Shown in /dev and the sweep output only (decision D4). */
    engineTargetReasons: z.array(z.string()).nullable().optional(),
});
/**
 * One agent of a run as its engine routed it at launch (runs.seats, migration 017): the root,
 * each roster member, `self`, each advisor. `specModel` is the id as authored and `canonical`
 * the catalog id it names; `served` is the model or deployment that actually ran (a Foundry
 * overlay can put `claude-sonnet-5` on `gpt-5.6-sol`), and `servedId` is the catalog id that
 * name belongs to, when the catalog knows it. `substituted`: Claude in the spec, another family
 * served.
 */
export const RunSeatSchema = z.object({
    role: z.enum(["root", "member", "self", "advisor"]),
    name: z.string(),
    specModel: z.string(),
    canonical: z.string(),
    provider: RuntimeProviderSchema,
    served: z.string(),
    servedId: z.string().optional(),
    substituted: z.boolean(),
});
/**
 * The credential that started a run (runs.started_by and runs.api_key_id, migration 042): the
 * session cookie, a bearer session, or an account API key with its id and, for the account's own
 * keys, the name it was issued with (an engine key's label is never shown). A key's run keeps the
 * key after it is revoked. A run from before the column reads as the cookie.
 */
export const RunStartedBySchema = z.object({
    kind: z.enum(["cookie", "session", "api_key"]),
    keyId: z.string().uuid().optional(),
    keyName: z.string().optional(),
});
/** GET /runs/:id — the summary plus session/usage/grading/capability surface. */
export const RunDetailSchema = RunSummarySchema.extend({
    // The kid-given agent name from the IMMUTABLE revision this run pinned —
    // a later draft rename never relabels an old run. Empty when the revision
    // spec carries no usable name; the client falls back to generic copy.
    agentName: z.string(),
    model: z.string().nullable(),
    credits: z.object({ held: z.number(), charged: z.number() }),
    usage: RunUsageSchema,
    // True only for the account's earliest satisfied run, and only once its
    // usage has settled — drives the one-time first-success celebration card.
    firstSatisfied: z.boolean(),
    finalizationStatus: RunFinalizationStateSchema,
    outputCount: z.number().int().nonnegative(),
    graders: z.array(z.object({
        source: z.string(),
        result: z.unknown(),
        model: z.string().nullable(),
        createdAt: z.string(),
    })),
    session: RunSessionInfoSchema.nullable(),
    // The ONLY spelling of the turn capabilities — they must survive a null
    // session (a pre-session run still reports canStartNew), so they never
    // appear on RunSessionInfoSchema too.
    canSend: z.boolean(),
    canInterrupt: z.boolean(),
    canResume: z.boolean(),
    canStartNew: z.boolean(),
    /**
     * Template Strategy V2 phase 3: the frozen profile this run executes on
     * (`<provider>/<canonical>`; null for runs reserved before the pin existed)
     * and the optional-capability limitations it proceeds under (the console
     * banner's durable source). Optional on the wire while clients learn them.
     */
    profileKey: z.string().nullable().optional(),
    limitations: z.array(LimitationSchema).optional(),
    /** The tools this run no longer asks for (Always allow), as toolGrantKey keys. */
    toolGrants: z.array(z.string()).optional(),
    /** The model each seat ran on, recorded at launch; null for a run from before that, or one whose spec did not route. */
    seats: z.array(RunSeatSchema).nullable().optional(),
    /** The credential that started the run; the server always sends it, optional on the wire while clients learn it. */
    startedBy: RunStartedBySchema.optional(),
    /**
     * The sequence of the run log's last event (-1 before any), read after the row: the last stored
     * row the stream and runs.events carry. A row outside the event vocabulary (session.usage) is
     * not counted, since no client folds it. A reader that holds every event up to it has nothing
     * of the log left to read as of the row it read. runs.get always sends it; optional on the wire
     * while clients learn it.
     */
    lastSequence: z
        .number()
        .int()
        .min(-1)
        .optional()
        .describe("The sequence of the run log's last event (the last stored row the stream and runs.events carry; -1 before any), read after the row. runs.get always sends it."),
});
/**
 * One row of the account-wide run history (GET /runs): the summary plus the
 * denormalized project/profile names, model, and charged credits so the
 * history list renders without a per-row fetch.
 */
export const HistoryRunSchema = RunSummarySchema.extend({
    projectName: z.string(),
    profileName: z.string().nullable(),
    model: z.string().nullable(),
    creditsCharged: z.number(),
    /** As on RunDetail: the credential that started the run. */
    startedBy: RunStartedBySchema.optional(),
});
/** One row of GET /skills — canonical `skill:` ids only. */
export const SkillSummarySchema = z.object({
    id: z.string().min(1),
    displayName: z.string(),
    skillName: z.string(),
    kind: z.enum(["anthropic", "custom"]),
    visibility: z.enum(["platform", "private"]),
    sizeBytes: z.number().int().nonnegative(),
    // The full DB vocabulary (migration 010) — delivery_unknown marks a push
    // whose provider delivery could not be reconciled and needs reconciliation.
    syncState: z.enum(["none", "pushing", "pushed", "error", "delivery_unknown"]),
});
/**
 * Memory rows as the workspace endpoints actually shape them: list/create/
 * update return a preview + size (archivedAt only on the list); the detail
 * GET returns full content + checksum WITHOUT preview/size. Deliberately two
 * schemas — the old web twin pretended detail extended entry and carried
 * fields the wire never had.
 */
export const MemoryEntrySchema = z.object({
    id: z.string().min(1),
    projectId: z.string().min(1),
    logicalPath: z.string().min(1),
    preview: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    archivedAt: z.string().nullable().optional(),
    updatedAt: z.string(),
    /** When the note was first written; optional on the wire while clients learn it. */
    createdAt: z.string().optional(),
    /** The run that last wrote the note (bookkeeping), or null for a note the owner wrote in Files. */
    updatedByRunId: z.string().nullable().optional(),
});
export const MemoryDetailSchema = z.object({
    id: z.string().min(1),
    projectId: z.string().min(1),
    logicalPath: z.string().min(1),
    content: z.string(),
    contentSha256: z.string(),
    archivedAt: z.string().nullable(),
    updatedAt: z.string(),
});
/** A project's memory scope (owner decision 29): a directory of notes its sessions join by slug. */
export const MemoryScopeSchema = z.object({
    slug: z.string().min(1),
    /** The memory store id a Claude-compatible session names to mount it on V5's own /v1. */
    storeId: z.string().min(1),
    /** Where it mounts in a session: /mnt/memory/<slug>. */
    mountPath: z.string().min(1),
    description: z.string().nullable(),
    createdAt: z.string(),
});
// --- MFA unified file explorer ---------------------------------------------
/**
 * The unified explorer contract (the legacy FileRef list contract was retired
 * 2026-07-16 with the /projects/:id/files routes). Source groups are the six
 * rail filters; entry kinds are the unique union discriminators.
 * Agent-created outputs and user-uploaded artifacts intentionally share the
 * user_artifact group and are distinguished by the server-derived `origin`.
 */
export const ExplorerSourceGroupSchema = z.enum([
    "upload",
    "user_artifact",
    "skill",
    "session_log",
    "memory",
    "yaml",
]);
export const ExplorerEntryKindSchema = z.enum([
    "upload",
    "user_artifact",
    "platform_skill",
    "custom_skill",
    "session_log",
    "memory",
    "project_yaml",
    "private_template_yaml",
    "platform_template_yaml",
]);
/** Canonical-id resource variant: `<prefix>:<uuid>` (case-insensitive uuid). */
const canonicalResource = (kind, prefix) => z
    .object({
    kind: z.literal(kind),
    id: z.string().regex(canonicalIdPattern(prefix)),
})
    .strict();
const fileResource = canonicalResource("file", "file");
const skillResource = canonicalResource("skill", "skill");
const memoryResource = canonicalResource("memory", "memory");
const sessionLogResource = canonicalResource("session_log", "session-log");
const projectYamlResource = z
    .object({ kind: z.literal("project_yaml"), projectId: z.string().uuid() })
    .strict();
const templateYamlResource = z
    .object({ kind: z.literal("template_yaml"), templateId: z.string().uuid() })
    .strict();
export const ExplorerResourceSchema = z.discriminatedUnion("kind", [
    fileResource,
    skillResource,
    memoryResource,
    sessionLogResource,
    projectYamlResource,
    templateYamlResource,
]);
export const ExplorerCapabilitiesSchema = z
    .object({
    preview: z.boolean(),
    download: z.boolean(),
    archive: z.boolean().optional(),
    delete: z.boolean(),
    editContent: z.boolean(),
    navigateEdit: z.boolean(),
    restore: z.boolean(),
})
    .strict();
export const ExplorerLifecycleStateSchema = z.enum([
    "available",
    "archived",
    "deleting",
    "delete_failed",
]);
const explorerEntryCommonShape = {
    entryKey: z.string().min(1).max(1024),
    displayName: z.string().min(1),
    displayPath: z.string().min(1),
    mountPath: z.string().min(1).nullable(),
    mediaType: z.string().min(1).nullable(),
    sizeBytes: z.number().int().nonnegative().nullable(),
    createdAt: z.string().datetime(),
    modifiedAt: z.string().datetime(),
    lifecycleState: ExplorerLifecycleStateSchema,
    origin: z.enum(["user", "agent", "system"]),
    originRunId: z.string().min(1).nullable(),
    capabilities: ExplorerCapabilitiesSchema,
    navigationTarget: z.string().min(1).optional(),
};
const explorerEntry = (entryKind, sourceGroup, resource) => z
    .object({
    ...explorerEntryCommonShape,
    entryKind: z.literal(entryKind),
    sourceGroup: z.literal(sourceGroup),
    resource,
})
    .strict();
export const ExplorerEntrySchema = z.discriminatedUnion("entryKind", [
    explorerEntry("upload", "upload", fileResource),
    explorerEntry("user_artifact", "user_artifact", fileResource),
    explorerEntry("platform_skill", "skill", skillResource),
    explorerEntry("custom_skill", "skill", skillResource),
    explorerEntry("session_log", "session_log", sessionLogResource),
    explorerEntry("memory", "memory", memoryResource),
    explorerEntry("project_yaml", "yaml", projectYamlResource),
    explorerEntry("private_template_yaml", "yaml", templateYamlResource),
    explorerEntry("platform_template_yaml", "yaml", templateYamlResource),
]);
export const ExplorerListQuerySchema = z
    .object({
    sourceGroup: ExplorerSourceGroupSchema.optional(),
    q: z.string().trim().min(1).max(200).optional(),
    after: z.string().min(1).max(2048).optional(),
    limit: z.number().int().min(1).max(200).default(50),
    runId: z.string().uuid().optional(),
    includeArchived: z.boolean().optional(),
})
    .strict();
export const ExplorerListResponseSchema = z
    .object({
    files: z.array(ExplorerEntrySchema),
    counts: z
        .object({
        all: z.number().int().nonnegative(),
        upload: z.number().int().nonnegative(),
        user_artifact: z.number().int().nonnegative(),
        skill: z.number().int().nonnegative(),
        session_log: z.number().int().nonnegative(),
        memory: z.number().int().nonnegative(),
        yaml: z.number().int().nonnegative(),
    })
        .strict(),
    nextCursor: z.string().min(1).max(2048).nullable(),
})
    .strict();
/**
 * Upload response envelope (POST /projects/:projectId/explorer/uploads). Only
 * the two fields the composer needs to render a chip are pinned here — `id` is
 * the canonical "file:<uuid>" id (decision 8: provider ids are never
 * application identifiers). Loose on purpose: the route returns the inserted
 * row (displayPath, mountPath, mediaType, sizeBytes, …) and this contract must
 * not silently strip fields it does not name.
 */
export const ExplorerUploadResponseSchema = z
    .object({
    file: z
        .object({
        id: z.string().regex(CANONICAL_FILE_ID),
        displayName: z.string().min(1),
    })
        .loose(),
})
    .loose();
/**
 * Run-exact attachment inventory. `stored` means the file exists in the
 * project but is not attached to this run's session (it will ride the next
 * job); the remaining states are the session-resource sync states.
 */
export const RunAttachmentStatusSchema = z.enum([
    "stored",
    "pending",
    "attaching",
    "attached",
    "delivery_unknown",
    "error",
    "detaching",
    "detached",
]);
/**
 * GET /runs/:runId/attachments — two independent dimensions per file.
 * `attachmentStatus` is inventory: whether the file is physically available to
 * this run's session. `pendingForMessage` is announcement: true while the file
 * is attached but not yet announced with a delivered user message, or still
 * unresolved. Composer chips key off `pendingForMessage` (plus unresolved
 * statuses) so a cleared chip never reappears, while Files keeps showing
 * availability.
 */
export const RunAttachmentsResponseSchema = z.object({
    files: z.array(z.object({
        // Canonical "file:" id — never a provider resource/file id (decision 8).
        fileId: z.string().min(1),
        displayName: z.string().min(1),
        attachmentStatus: RunAttachmentStatusSchema,
        pendingForMessage: z.boolean(),
    })),
});
/** One agent, or a team: a template whose agent.yaml has a `multiagent` block. Derived, never authored. */
export const CatalogTemplateKindSchema = z.enum(["single", "team"]);
/**
 * Where a template runs, one provider and runner (wire 5.22.0, V6 plan row H.27, designer H36): the
 * verdict the publish derived for the pair (catalog_entries.needs, what catalog.detail's `needs`
 * carries with its rule ids) and whether this deployment serves the pair now. `verdict` is null
 * for a pair nothing checked (Foundry's, held). A pair whose findings name `engine_routing` reads
 * blocked: a start on it is refused even where the checker's finding is only advisory (the
 * Anthropic passthrough). A start still re-checks.
 */
export const CatalogRunsOnSchema = z.object({
    provider: RuntimeProviderSchema,
    runner: z.enum(ENGINE_TARGETS),
    verdict: z.enum(["ok", "degraded", "blocked"]).nullable(),
    served: z.boolean(),
});
/**
 * Another live template a catalog item points at — a team's member, or a team a
 * single sits in — with its title in the same locale. It opens at
 * `/templates/:libraryKey/:slug`.
 */
export const CatalogTemplateRefSchema = z.object({
    libraryKey: z.string().min(1),
    slug: z.string().min(1),
    name: z.string().min(1),
});
/**
 * What a template can do, derived by the server from its agent.yaml: `web` (web
 * search or web fetch enabled), `runs-code` (bash enabled), `connectors` (an MCP
 * toolset), `skills` (any skill). The wire carries plain strings, so a key added
 * later never fails an older client.
 */
export const CATALOG_CAPABILITY_KEYS = ["web", "runs-code", "connectors", "skills"];
/**
 * Public (signed-out) catalog item: platform-published templates only, with
 * catalog facets and WITHOUT prompt text — the full spec is only reachable
 * after sign-in via project creation. Detail URLs pair libraryKey with slug
 * because slugs are only library-unique.
 */
export const CatalogTemplateSchema = z.object({
    templateId: z.string().min(1),
    templateVersionId: z.string().min(1),
    /** The template's home site (where its content lives); both sites list and serve it. */
    siteId: SiteIdSchema,
    libraryId: z.string().min(1),
    libraryKey: z.string().min(1),
    libraryName: z.string().min(1),
    slug: z.string().min(1),
    version: z.number().int().positive(),
    locale: LocaleSchema,
    name: z.string().min(1),
    description: z.string(),
    categoryKey: z.string().nullable(),
    tagKeys: z.array(z.string()),
    /**
     * Whose work it serves: keys of `industries` in content/taxonomy.yaml, as the live
     * version's catalog.yaml names them; [] until its sidecar does. Defaulted so a payload
     * from before the field still parses.
     */
    industryKeys: z.array(z.string()).default([]),
    // Derived from the live version when the content tree is published (catalog_entries);
    // defaulted so a payload from before these fields still parses.
    kind: CatalogTemplateKindSchema.default("single"),
    /** A team's roster in roster order (`self` and the advisor are not members); [] for a single. */
    members: z.array(CatalogTemplateRefSchema).default([]),
    /** The live teams, on either site, that name this single, in catalog order; [] for a team. */
    memberOf: z.array(CatalogTemplateRefSchema).default([]),
    /**
     * CATALOG_CAPABILITY_KEYS that the live version's agent.yaml enables; for a team, what it
     * or any member enables.
     */
    capabilityKeys: z.array(z.string()).default([]),
    /**
     * When the live version first went live on the template's home site (ISO 8601). Optional:
     * a payload from before the field, or a row published outside the content publish, has none.
     */
    publishedAt: z.string().optional(),
    /**
     * Where it runs: one entry per provider and runner (RUNTIME_PROVIDERS × ENGINE_TARGETS, in
     * that order) for the live version, CatalogRunsOnSchema (wire 5.22.0, H.27); [] for a version
     * that is not the live one. Optional only so a payload from before the field still parses: a
     * server since 5.22.0 always sends it.
     */
    runsOn: z.array(CatalogRunsOnSchema).optional(),
});
/**
 * A catalog snapshot row: the catalog row as `list` serves it. Its optional `publishedAt` (when
 * the live version went live) orders the gallery's "Newest"; without it the gallery hides that
 * order.
 */
export const CatalogSnapshotTemplateSchema = CatalogTemplateSchema;
/**
 * One term of the shared template vocabulary (content/taxonomy.yaml): its key, its label in
 * the request locale (English when that locale has none) and, when the file gives one, a
 * line of English that says what belongs under it.
 */
export const CatalogVocabularyTermSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    definition: z.string().min(1).optional(),
});
/**
 * The vocabulary both sites share, each list in the file's order: `categories` (what work
 * the agent does), `industries` (whose work it serves), `keywords` (the collections a
 * template's `tagKeys` may name, `original` among them) and `capabilities` (what
 * `capabilityKeys` may name). The whole file, not only the terms a live template uses: the
 * browser counts.
 */
export const CatalogVocabularySchema = z.object({
    categories: z.array(CatalogVocabularyTermSchema).default([]),
    industries: z.array(CatalogVocabularyTermSchema).default([]),
    keywords: z.array(CatalogVocabularyTermSchema).default([]),
    capabilities: z.array(CatalogVocabularyTermSchema).default([]),
});
/** A git commit, pinned: the full 40-character SHA, never a branch, a tag or HEAD. */
export const GIT_COMMIT_SHA_PATTERN = /^[0-9a-f]{40}$/;
/**
 * Where a library's content comes from (V6 plan 13.3.3 "What a library author writes", H.7):
 * `spec.attribution` in its library file, required when the library copies third-party
 * content. `repo` is the upstream's https URL, `commit` the SHA the copy was taken at (the
 * content gate also refuses a mutable reference such as `…@main` anywhere in the file),
 * `license` its licence (an SPDX id such as `MIT` or `Apache-2.0`) and `holder` the copyright
 * holder the notice names. Every field is required and no other is accepted.
 */
export const LibraryAttributionSchema = z
    .object({
    repo: z.string().trim().max(300).url().startsWith("https://"),
    commit: z.string().regex(GIT_COMMIT_SHA_PATTERN, {
        message: "commit must be a full 40-character lowercase SHA, never a branch, a tag or HEAD",
    }),
    license: z.string().trim().min(1).max(100),
    holder: z.string().trim().min(1).max(200),
})
    .strict();
/** The file a template directory names its own upstream in (H.26). */
export const TEMPLATE_SOURCE_FILE = "SOURCE.json";
/** A path inside the upstream repository: relative, and never climbing out of it. */
const UpstreamPathSchema = z
    .string()
    .trim()
    .min(1)
    .max(300)
    .refine((value) => !value.startsWith("/") && !value.split("/").includes(".."), {
    message: "a path inside the upstream repository, relative and without ..",
});
/**
 * A template's `SOURCE.json` (V6 plan 13.3.3 "What a library author writes", H.26): what a
 * template copied from an upstream other than its library's names, with the library
 * attribution's four fields, the upstream `paths` it drew on and the `change` V5 made. The
 * content gate refuses one whose repo has no section in THIRD_PARTY_NOTICES.md. Strict: no
 * other key is accepted.
 */
export const TemplateSourceSchema = LibraryAttributionSchema.extend({
    paths: z.array(UpstreamPathSchema).min(1).max(100),
    change: z.string().trim().min(1).max(2000),
}).strict();
/**
 * A published library as the catalog reads it (H.7): its key, its name and description in the
 * request locale (falling back to English), its home site, its attribution (null when the
 * library file declares none) and how many of its templates are live. Only a library that
 * holds a live template is listed, so `templateCount` is at least 1 and is the number of
 * snapshot rows with its `libraryKey`.
 */
export const CatalogLibrarySchema = z.object({
    key: z.string().min(1),
    name: z.string().min(1),
    siteId: SiteIdSchema,
    description: z.string(),
    attribution: LibraryAttributionSchema.nullable(),
    templateCount: z.number().int().min(1),
});
/**
 * A template by its library and slug, as a shelf names it: never a template id, so a shelf
 * survives republishing (V6 plan §13.3.3 "A shelf"). The pair opens at
 * `/templates/:libraryKey/:slug`.
 */
export const CatalogShelfRefSchema = z.object({
    libraryKey: z.string().min(1),
    slug: z.string().min(1),
});
/**
 * What a curated row lists when it names no templates: the live templates that carry the
 * category, any of the industries, any of the keywords and any of the capabilities it names
 * (each a key of the shared vocabulary; a list it leaves empty narrows nothing).
 */
export const CatalogShelfFilterSchema = z.object({
    category: z.string().min(1).optional(),
    industries: z.array(z.string().min(1)),
    keywords: z.array(z.string().min(1)),
    capabilities: z.array(z.string().min(1)),
});
/**
 * A curated row a front-end may feature: its label in the request locale, and either the
 * templates it names, in order, or a filter over the shelf.
 */
export const CatalogShelfRowSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    templates: z.array(CatalogShelfRefSchema),
    filter: CatalogShelfFilterSchema.optional(),
});
/**
 * A front-end's view of the catalog (`content/shelves/<id>.yaml`; the first-party shelves' ids
 * are the site ids): which libraries or keywords it lists, what it keeps off, the templates
 * that lead it unfiltered, whether the other blanks are hidden, whose templates the
 * Recommended order puts first, and its curated rows. It changes what is listed, never what
 * runs; `catalog-shelf.ts` in contracts evaluates it for every client.
 */
export const CatalogShelfSchema = z.object({
    id: z.string().min(1),
    /** `all`, or the library keys it lists; a template carrying one of `keywords` is listed too. */
    lists: z.object({
        libraries: z.union([z.literal("all"), z.array(z.string().min(1))]),
        keywords: z.array(z.string().min(1)),
    }),
    /** Library keys and templates kept off the shelf, pinned or not. */
    excludes: z.object({
        libraries: z.array(z.string().min(1)),
        templates: z.array(CatalogShelfRefSchema),
    }),
    /** The templates that lead the unfiltered shelf, in this order. */
    pinned: z.array(CatalogShelfRefSchema),
    /** Every blank that is not pinned is left off the shelf (its URL still opens). */
    hideBlanks: z.boolean(),
    /** Whose templates the Recommended order puts first; absent, no site is. */
    rankFirst: z.object({ site: SiteIdSchema }).optional(),
    rows: z.array(CatalogShelfRowSchema),
});
/**
 * The whole shelf in one cacheable answer (`GET ${API_PREFIX}/catalog/snapshot?locale=xx`):
 * every live template of both sites in the locale, in title order, the libraries they sit in,
 * the shared vocabulary labelled in that locale, and the shelves. `catalogVersion` changes
 * when a publication goes live or is retired, the publish rewrites a catalog entry or writes a
 * library version, so it keys the response's ETag; the gallery filters, counts and orders in
 * the browser (owner decision D5 of workflows/V5-Template-Method-Review.md).
 */
export const CatalogSnapshotSchema = z.object({
    catalogVersion: z.string().min(1),
    locale: LocaleSchema,
    templates: z.array(CatalogSnapshotTemplateSchema),
    vocabulary: CatalogVocabularySchema.default({
        categories: [],
        industries: [],
        keywords: [],
        capabilities: [],
    }),
    /**
     * Every library that holds a live template, by key (wire 5.15.0, H.7): the same entries as
     * `catalog.facets`. Defaulted, so a snapshot from an older server still parses.
     */
    libraries: z.array(CatalogLibrarySchema).default([]),
    /**
     * Every shelf in content/shelves/, rows labelled in the locale (wire 5.17.0, H.24); a
     * front-end reads its own by id. Defaulted, so a snapshot from an older server still parses.
     */
    shelves: z.array(CatalogShelfSchema).default([]),
});
/**
 * A library's own categories and tags: always empty. Templates take their category and
 * keywords from the shared vocabulary (`vocabulary`, content/taxonomy.yaml); the two lists
 * stay on `catalog.facets` only because the wire changes by addition (a later /api/v6 drops
 * them). Read `vocabulary` instead.
 */
const RetiredLibraryTermsSchema = z
    .array(z.object({ key: z.string().min(1), label: z.string().min(1) }))
    .max(0)
    .meta({
    deprecated: true,
    description: "Always empty: a template's category and keywords come from the shared vocabulary (`vocabulary`). Kept so the wire changes only by addition.",
});
export const CatalogFacetsSchema = z.object({
    /** Every library that holds a live template, by key: CatalogLibrarySchema and the two retired lists. */
    libraries: z.array(CatalogLibrarySchema.extend({
        categories: RetiredLibraryTermsSchema,
        tags: RetiredLibraryTermsSchema,
    })),
    /** The shared vocabulary with labels; defaulted (empty lists) so an older payload still parses. */
    vocabulary: CatalogVocabularySchema.default({
        categories: [],
        industries: [],
        keywords: [],
        capabilities: [],
    }),
});
// The one error body (V6 plan 13.3.1 "The wire" → Errors, H.16). Every failure a client of
// /api/v5 can meet answers at its HTTP status with this object, whatever path it came in on: a
// procedure's own failure on the plain JSON wire, and everything that answers before or beside a
// procedure (the credential, the Origin check, the site, a hand-written route, an unknown path,
// the body ceiling, a body the wire does not read). Before H.19 a procedure in the RPC encoding
// (the /api/v5/rpc alias) answered it inside `{"json": …}`. /v1, the Anthropic-SDK-compatible
// surface, keeps the SDK's own error envelope. A client reads it with readApiError
// (api/errors.ts), which also reads the two bodies it replaced and the RPC wrap.
/** The code of a request that fails its input schema; the zod issues are in `data.issues`. */
export const VALIDATION_ERROR_CODE = "BAD_REQUEST";
/** The code of a failure the server did not expect: a 500, logged under its trace id. */
export const INTERNAL_ERROR_CODE = "internal_error";
export const ApiErrorBodySchema = z.object({
    code: z
        .string()
        .min(1)
        .describe("The stable identifier a client branches on: run_not_found, api_key_forbidden, rate_limited, BAD_REQUEST for a failed input schema, internal_error for a failure the server did not expect."),
    status: z.number().int().min(400).max(599).describe("The response's HTTP status, repeated."),
    message: z
        .string()
        .describe("Text for a log or a developer, never parsed: the code, or for BAD_REQUEST the validator's sentence."),
    data: z
        .record(z.string(), z.unknown())
        .describe("The code's parameters ({maxCents} for run_budget_too_high, {cap, used, resetsAt} for daily_run_cap_reached, {issues} for BAD_REQUEST); {} when it has none."),
    traceId: z
        .string()
        .min(1)
        .describe("The request id the response's x-request-id header carries: quote it to find the request in the server's logs."),
});
// --- Admin Overview (Admin Foundation, read-only) ---------------------------
// Ordered finest to coarsest — this is also the display order of the Model
// Costing Sheet's Window select. Sub-day windows exist for the live costing
// view; the overview endpoint accepts them too (same scope schema).
export const ADMIN_OVERVIEW_WINDOWS = [
    "30m",
    "1h",
    "3h",
    "6h",
    "12h",
    "1d",
    "3d",
    "5d",
    "7d",
    "30d",
    "90d",
];
export const AdminOverviewWindowSchema = z.enum(ADMIN_OVERVIEW_WINDOWS);
/**
 * Aggregate-only metrics. Nothing in this shape may ever carry names, emails,
 * prompts, missions, raw errors/logs, YAML, provider ids, or per-account rows.
 * `runs.byStatus` is an exhaustive record over the runs.status vocabulary —
 * zod 4 requires every key, which IS the zero-fill guarantee.
 */
export const AdminOverviewMetricsSchema = z.object({
    accounts: z.object({
        customers: z.number().int(),
        admins: z.number().int(),
        active: z.number().int(),
        suspended: z.number().int(),
        closed: z.number().int(),
        customersCreatedInWindow: z.number().int(),
    }),
    profiles: z.object({ active: z.number().int(), archived: z.number().int() }),
    subscriptions: z.object({
        active: z.number().int(),
        pastDue: z.number().int(),
        canceled: z.number().int(),
        planDistribution: z.array(z.object({ planId: z.string(), planName: z.string(), count: z.number().int() })),
    }),
    templates: z.object({
        platform: z.number().int(),
        private: z.number().int(),
        archived: z.number().int(),
        liveOfficial: z.number().int(),
        marketplaceListed: z.number().int(),
    }),
    runs: z.object({
        byStatus: z.record(RunStatusSchema, z.number().int()),
        total: z.number().int(),
        settledCreditsCharged: z.number().int(),
    }),
    operations: z.object({
        jobsQueued: z.number().int(),
        jobsRunning: z.number().int(),
        jobsDead: z.number().int(),
        deliveryUnknown: z.object({
            providerCreations: z.number().int(),
            skills: z.number().int(),
            workspaceFiles: z.number().int(),
            commands: z.number().int(),
            sessionFileResources: z.number().int().nonnegative(),
            /** Loop-provider steps parked delivery_unknown (a model call that may have run upstream). */
            loopSteps: z.number().int().nonnegative(),
            /** Loop remote tool calls left in_flight past the executor's turnaround (outcome lost). */
            loopToolCalls: z.number().int().nonnegative(),
        }),
    }),
});
export const AdminOverviewResponseSchema = z.object({
    scope: z.object({
        site: z.union([SiteIdSchema, z.literal("all")]),
        window: AdminOverviewWindowSchema,
        from: z.string(),
        to: z.string(),
    }),
    generatedAt: z.string(),
    // Cross-site totals are sums over per-site rows — site-account sums, not
    // unique people (the same human may hold an account on each site).
    totals: AdminOverviewMetricsSchema,
    sites: z.array(z.object({ siteId: SiteIdSchema, metrics: AdminOverviewMetricsSchema })),
});
// --- Admin Costing (realtime costing sheet, read-only) ----------------------
/**
 * Aggregate-only, like the Overview: nothing here may ever carry names,
 * emails, prompts, missions, raw errors, YAML, provider ids, or per-account
 * rows. Dimensions are bounded vocabularies (model ids from the price
 * book/plan allow-lists plus "unknown"; plan ids from the plans table), and
 * rows are flat and site-tagged — costing dimensions are already per-site.
 *
 * Time semantics: observedByModel and totals honor the requested window over
 * SETTLED usage by run_usage.settled_at (migration 077); planSnapshot is
 * calendar-month-to-date (the billing period cap enforcement uses),
 * regardless of the window; priceBook covers rows overlapping the window
 * plus current and upcoming.
 */
export const AdminCostingPriceRowSchema = z.object({
    modelId: z.string(),
    inputPerMTokCents: z.number().int(),
    outputPerMTokCents: z.number().int(),
    cacheWritePerMTokCents: z.number().int(),
    cacheReadPerMTokCents: z.number().int(),
    effectiveFrom: z.string(),
    effectiveUntil: z.string().nullable(),
    /** window = superseded but overlapped the requested window. */
    status: z.enum(["window", "active", "upcoming"]),
});
export const AdminCostingModelRowSchema = z.object({
    siteId: SiteIdSchema,
    /**
     * The run's SELECTED session model ("unknown" when the run never reached a
     * session). Fallback/server grading can add spend from a different
     * provider model; that spend stays inside this row's cost figures.
     */
    selectedAgentModel: z.string(),
    runs: z.number().int(),
    /**
     * Coverage: settlement stores NULL costs (while still charging hold
     * credits) when no price-book row matched. SQL sums would silently read
     * those as zero-cost, so priced/unpriced counts are explicit and every
     * ratio below derives from the priced cohort only.
     */
    pricedRuns: z.number().int(),
    unpricedRuns: z.number().int(),
    avgAgentInputTokens: z.number().nullable(),
    avgAgentOutputTokens: z.number().nullable(),
    /** Agent-only: cache_read / (input + cache_read + cache_write). */
    cacheReadShare: z.number().nullable(),
    /** Priced cohort, all-in: token + web-search + runtime cost cents. */
    knownAllInCostCents: z.number(),
    avgAllInCostCentsPerPricedRun: z.number().nullable(),
    creditsChargedPricedCohort: z.number().int(),
    creditsChargedTotal: z.number().int(),
    /** knownAllInCostCents / creditsChargedPricedCohort; null on empty cohort. */
    observedCentsPerCredit: z.number().nullable(),
});
/**
 * The runtime a settled run executed on: the bounded RUNTIME_PROVIDERS
 * vocabulary plus this aggregate's "unknown" sentinel (a run that never
 * reached a session pins no provider). Never a free-form string.
 */
export const AdminCostingRuntimeProviderSchema = z.union([
    RuntimeProviderSchema,
    z.literal("unknown"),
]);
/**
 * observedByModel split one level finer, by (site, selected model, runtime).
 * Since migration 106 the price book holds canonical per-MODEL ceilings and
 * provider unit-cost differences are margin, so the same model on Anthropic
 * vs OpenRouter vs Foundry must stay distinguishable in the one sheet that
 * judges the ceiling. Same figures and priced-cohort semantics as the model
 * rows, which are these rows rolled up.
 */
export const AdminCostingProviderRowSchema = AdminCostingModelRowSchema.extend({
    runtimeProvider: AdminCostingRuntimeProviderSchema,
});
export const AdminCostingPlanRowSchema = z.object({
    siteId: SiteIdSchema,
    planId: z.string(),
    planName: z.string(),
    priceCentsMonth: z.number().int(),
    monthlyCreditCap: z.number().int().nullable(),
    /** Non-canceled subscriptions currently on the plan. */
    activeSubscriberCount: z.number().int(),
    /**
     * CURRENT-PLAN-ATTRIBUTED SNAPSHOT: one mutable subscription row exists
     * per account and ledger entries snapshot no plan/price, so a mid-month
     * plan switch moves that account's whole month under the new tier. Exact
     * tier economics needs subscription history — deliberately out of scope.
     *
     * Credit numbers use billing's own ledger semantics: gross charges,
     * same-period matched goal-help refunds, net = gross − refunds, and
     * reserved = outstanding run holds (deliberately not date-filtered).
     */
    grossChargesCreditsMtd: z.number().int(),
    matchedRefundCreditsMtd: z.number().int(),
    netSettledCreditsMtd: z.number().int(),
    reservedCredits: z.number().int(),
    monthSettledRuns: z.number().int(),
    pricedRunsMtd: z.number().int(),
    unpricedRunsMtd: z.number().int(),
    /** RUN-ONLY: the goal helper charges credits but persists no provider cost. */
    knownRunComputeCostMtdCents: z.number(),
    /** activeSubscriberCount × priceCentsMonth — LIST price. No payment system
     *  exists yet, so this is not collected or recognized revenue. */
    listPriceMrrCents: z.number().int(),
    /** ESTIMATE ONLY: list MRR − known run compute. Not gross margin (list
     *  price ≠ collected revenue; partial month; run-only compute). */
    listPriceMrrLessMtdComputeCents: z.number(),
    /** Pooled fleet average: 100 × netSettledCreditsMtd /
     *  (activeSubscriberCount × monthlyCreditCap); null when uncapped or no
     *  subscribers. NOT a mean of per-account utilizations. */
    avgUtilizationPct: z.number().nullable(),
});
export const AdminCostingTotalsSchema = z.object({
    settledRuns: z.number().int(),
    pricedRuns: z.number().int(),
    unpricedRuns: z.number().int(),
    /** 100 × pricedRuns / settledRuns; null when there are no runs. */
    costCoveragePct: z.number().nullable(),
    knownAllInCostCents: z.number(),
    creditsChargedTotal: z.number().int(),
    creditsChargedPricedCohort: z.number().int(),
    avgAllInCostCentsPerPricedRun: z.number().nullable(),
    avgCreditsPerRun: z.number().nullable(),
    /** Agent + grader combined, cache writes in the denominator — the
     *  economics view of caching. */
    cacheReadShare: z.number().nullable(),
    /** Observed raw-compute cents per credit over the priced cohort. The
     *  static sheet ASSUMES ~0.6¢/credit with per-run ladders; the engine
     *  CHARGES cost-based at CREDIT_CENTS (2¢). Surfaced side by side. */
    observedCentsPerCredit: z.number().nullable(),
});
export const AdminCostingResponseSchema = z.object({
    scope: z.object({
        site: z.union([SiteIdSchema, z.literal("all")]),
        window: AdminOverviewWindowSchema,
        from: z.string(),
        to: z.string(),
    }),
    /** The billing period planSnapshot covers (Postgres date_trunc('month')). */
    monthToDate: z.object({ from: z.string(), to: z.string() }),
    generatedAt: z.string(),
    priceBook: z.array(AdminCostingPriceRowSchema),
    observedByModel: z.array(AdminCostingModelRowSchema),
    /**
     * The per-runtime split of observedByModel (added 2026-08-21). The server
     * always emits it; optional in the contract only so payloads and fixtures
     * that predate the runtime dimension still parse.
     */
    observedByProvider: z.array(AdminCostingProviderRowSchema).optional(),
    planSnapshot: z.array(AdminCostingPlanRowSchema),
    totals: AdminCostingTotalsSchema,
});
export const EXPERIENCE_KEYS = [
    { key: "auth.eyebrow.mfa", maxLength: 120, required: true },
    { key: "auth.eyebrow.lya", maxLength: 120, required: true },
    { key: "auth.hero.mfa", maxLength: 160, required: true },
    { key: "auth.hero.lya", maxLength: 160, required: true },
    { key: "auth.body.mfa", maxLength: 300, required: true },
    { key: "auth.body.lya", maxLength: 300, required: true },
    { key: "auth.title", maxLength: 80, required: true },
    { key: "profile.empty.mfa", maxLength: 200, required: true },
    { key: "profile.empty.lya", maxLength: 200, required: true },
    { key: "templates.empty", maxLength: 200, required: true },
    { key: "console.empty", maxLength: 200, required: true },
];
export const ExperienceValueSchema = z.object({
    en: z.string(),
    es: z.string(),
});
export const ExperienceValuesSchema = z.record(z.string(), ExperienceValueSchema);
// --- Content authoring (template library management suite) -----------------
export const CONTENT_API_VERSION = "myfirstagent.ai/content/v1";
/**
 * V2 meters every run with one flat credit hold (migration 022 translated
 * V1's per-model credit numbers by run-count parity). The single source for
 * both the server's hold amount and any UI "~N credits per run" copy.
 */
export const RUN_FLAT_CREDITS = 100;
export const ContentKeySchema = z
    .string()
    .regex(/^[a-z0-9]+(?:[_-][a-z0-9]+)*$/)
    .max(80);
export const TemplateTranslationSchema = z.object({
    title: z.string().trim().min(1).max(120),
    description: z.string().max(1000).default(""),
    prompt: z
        .object({
        system: z.string().min(1).max(100_000),
        goal: z.string().max(2000).optional(),
    })
        .optional(),
});
/**
 * Authoring cap for template tags. The storage schema below deliberately
 * stays at max(20) so pre-existing content (and old immutable versions)
 * remains readable and restorable; the 10-tag ceiling is enforced by the
 * editor JSON Schema and by authoring validation before a new version can be
 * saved or published.
 */
export const TEMPLATE_TAGS_AUTHORING_LIMIT = 10;
export const ContentMetadataSchema = z.object({
    key: ContentKeySchema,
    authoredLocale: LocaleSchema,
    category: ContentKeySchema.optional(),
    tags: z.array(ContentKeySchema).max(20).default([]),
    audienceBands: z.array(z.string().min(1)).max(10).default([]),
});
const requireAuthoredLocaleTranslation = (manifest, context) => {
    if (!manifest.translations[manifest.metadata.authoredLocale]) {
        context.addIssue({
            code: "custom",
            path: ["translations", manifest.metadata.authoredLocale],
            message: `translations must include the authored locale "${manifest.metadata.authoredLocale}"`,
        });
    }
};
const flagIndexedDuplicates = (context, entries, keyOf, pathOf, messageOf) => {
    const seen = new Set();
    entries.forEach((entry, index) => {
        const key = keyOf(entry);
        if (seen.has(key)) {
            context.addIssue({ code: "custom", path: pathOf(index), message: messageOf(key) });
        }
        seen.add(key);
    });
};
/**
 * Strict Messages-API import shape (two-format ingest, one-format authoring).
 * The honest subset the converter maps is typed precisely; known-but-dropped
 * generation/config knobs parse loosely so the converter can flag each one;
 * anything else fails strict parsing and surfaces as a per-field diagnostic
 * instead of being silently ignored. Doubles as the Monaco JSON-Schema source
 * for the Messages editing buffer.
 */
export const MessagesContentBlockSchema = z.looseObject({
    type: z.enum(["text", "document", "image"]),
    text: z.string().optional(),
});
export const MessagesMessageSchema = z.strictObject({
    role: z.enum(["user", "assistant"]),
    content: z.union([z.string(), z.array(MessagesContentBlockSchema)]),
});
export const MessagesImportSchema = z.strictObject({
    model: z.string().min(1),
    messages: z.array(MessagesMessageSchema),
    system: z.union([z.string(), z.array(MessagesContentBlockSchema)]).optional(),
    tools: z
        .array(z.looseObject({
        type: z.string().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        input_schema: z.record(z.string(), z.unknown()).optional(),
    }))
        .optional(),
    mcp_servers: z.array(z.looseObject({ name: z.string(), url: z.string() })).optional(),
    container: z
        .looseObject({
        skills: z
            .array(z.looseObject({ type: z.string().optional(), skill_id: z.string().optional() }))
            .optional(),
    })
        .optional(),
    // Known-but-dropped knobs: they parse so the converter can flag each one.
    temperature: z.unknown().optional(),
    max_tokens: z.unknown().optional(),
    top_p: z.unknown().optional(),
    top_k: z.unknown().optional(),
    stop_sequences: z.unknown().optional(),
    thinking: z.unknown().optional(),
    output_config: z.unknown().optional(),
    metadata: z.unknown().optional(),
    stream: z.unknown().optional(),
    service_tier: z.unknown().optional(),
});
const JSON_SCHEMA_OPTIONS = { io: "input", target: "draft-7" };
let messagesImportJsonSchemaCache;
/** JSON Schema (draft-07) for the Messages-API import buffer. */
export function messagesImportJsonSchema() {
    messagesImportJsonSchemaCache ??= z.toJSONSchema(MessagesImportSchema, JSON_SCHEMA_OPTIONS);
    return messagesImportJsonSchemaCache;
}
export const ValidationSeveritySchema = z.enum(["error", "warning", "info"]);
export const ValidationDiagnosticSchema = z.object({
    severity: ValidationSeveritySchema,
    code: z.string().min(1),
    message: z.string().min(1),
    path: z.string().optional(),
    line: z.number().int().positive().optional(),
    column: z.number().int().positive().optional(),
    remediation: z.string().optional(),
});
export const ValidationReportSchema = z.object({
    ok: z.boolean(),
    diagnostics: z.array(ValidationDiagnosticSchema),
});
export const ContentResourceTypeSchema = z.enum(["template", "library"]);
export const ASSET_ID_PATTERN = canonicalIdPattern("asset");
export const LAYOUT_PRESETS = ["grid", "list", "showcase"];
export const LayoutPresetSchema = z.enum(LAYOUT_PRESETS);
export const THEME_TOKEN_KEYS = [
    "ink",
    "surface",
    "surfaceSubtle",
    "paper",
    "primary",
    "primaryStrong",
    "accent",
    "success",
    "danger",
    "muted",
];
export const TaxonomyEntrySchema = z.object({
    key: ContentKeySchema,
    labels: z.partialRecord(LocaleSchema, z.string().trim().min(1).max(80)),
});
export const LibraryTranslationSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().max(1000).default(""),
})
    .strict();
/**
 * A library file's `spec`. Strict, like the rest of the Library shape (H.7): an unknown key is
 * a gate failure, never silently dropped, so a misplaced field cannot pass unnoticed.
 */
export const LibrarySpecSchema = z
    .object({
    /**
     * Optional, and empty in every platform library file: templates take their category and
     * keywords from content/taxonomy.yaml (workflows/V5-Template-Organization.md Rev 2.1 PR-9).
     */
    categories: z.array(TaxonomyEntrySchema).max(40).default([]),
    tags: z.array(TaxonomyEntrySchema).max(100).default([]),
    themeTokens: z
        .partialRecord(z.enum(THEME_TOKEN_KEYS), z.string().trim().min(1).max(64))
        .default({}),
    layoutPreset: LayoutPresetSchema.optional(),
    logoAsset: z.string().regex(ASSET_ID_PATTERN).optional(),
    /** Where the library's content comes from; absent for first-party content. */
    attribution: LibraryAttributionSchema.optional(),
})
    .strict();
/**
 * The one library-file shape, `libraries/<site>/<key>.yaml` (H.7): strict at every level, so a
 * file in any other shape (the export's old plain record included) fails the content gate.
 */
export const LibraryManifestSchema = z
    .object({
    apiVersion: z.literal(CONTENT_API_VERSION),
    kind: z.literal("Library"),
    metadata: z.object({ key: ContentKeySchema, authoredLocale: LocaleSchema }).strict(),
    translations: z.partialRecord(LocaleSchema, LibraryTranslationSchema),
    spec: LibrarySpecSchema,
})
    .strict()
    .superRefine((manifest, context) => {
    requireAuthoredLocaleTranslation(manifest, context);
    for (const [field, entries] of [
        ["categories", manifest.spec.categories],
        ["tags", manifest.spec.tags],
    ]) {
        flagIndexedDuplicates(context, entries, (entry) => entry.key, (index) => ["spec", field, index, "key"], (key) => `duplicate ${field} key "${key}"`);
    }
});
export const CRITERION_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const RubricSchema = z
    .object({
    scale: z.object({
        minimum: z.number().int().nonnegative(),
        maximum: z.number().int().positive(),
    }),
    criteria: z
        .array(z.object({
        id: z.string().regex(CRITERION_ID_PATTERN).max(80),
        weight: z.number().positive().max(100),
        labels: z.partialRecord(LocaleSchema, z.string().trim().min(1).max(200)).optional(),
    }))
        .min(1)
        .max(20),
})
    .superRefine((rubric, context) => {
    if (rubric.scale.maximum <= rubric.scale.minimum) {
        context.addIssue({
            code: "custom",
            path: ["scale", "maximum"],
            message: "scale.maximum must be greater than scale.minimum",
        });
    }
    flagIndexedDuplicates(context, rubric.criteria, (criterion) => criterion.id, (index) => ["criteria", index, "id"], (key) => `duplicate criterion id "${key}"`);
});
export const ProjectTemplateUpgradeSchema = z.object({
    projectId: z.string().min(1),
    appliedVersion: z
        .object({ id: z.string().min(1), version: z.number().int().positive() })
        .nullable(),
    latestVersion: z
        .object({ id: z.string().min(1), version: z.number().int().positive() })
        .nullable(),
    upgradeAvailable: z.boolean(),
});
//# sourceMappingURL=wire.js.map